/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

import { useState } from 'react'
import { X, Brain } from 'lucide-react'
import { PERSONA_LIBRARY, PERSONA_CATEGORIES, PersonaTemplate } from '@/core/personas'
import { OPENROUTER_MODELS } from '@/providers/llm-provider'

interface AddPeerModalProps {
  onClose: () => void
  onAdd: (persona: PersonaTemplate, modelId: string) => void
}

export function AddPeerModal({ onClose, onAdd }: AddPeerModalProps) {
  const [selectedCategory, setSelectedCategory] = useState<string>(PERSONA_CATEGORIES[0])
  const [selectedPersona, setSelectedPersona] = useState<PersonaTemplate | null>(null)
  const [selectedModel, setSelectedModel] = useState<string>('')
  const [thinkingMode, setThinkingMode] = useState(false)

  const filteredPersonas = PERSONA_LIBRARY.filter(p => p.category === selectedCategory)

  const handleAdd = () => {
    if (selectedPersona && selectedModel) {
      onAdd(selectedPersona, selectedModel)
      onClose()
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-8"
      onClick={onClose}
    >
      <div
        className="bg-background-secondary border border-border rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border p-4">
          <div className="flex items-center gap-3">
            <Brain className="w-6 h-6 text-accent" />
            <h2 className="text-lg font-title">Add New Peer</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-background-tertiary rounded transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6 space-y-6">
          {/* Category Selection */}
          <div>
            <label className="block text-sm font-condensed text-foreground-secondary mb-2">
              Category
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => {
                setSelectedCategory(e.target.value)
                setSelectedPersona(null)
              }}
              className="w-full px-3 py-2 bg-background-tertiary border border-border rounded text-sm focus:outline-none focus:border-accent"
            >
              {PERSONA_CATEGORIES.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          {/* Persona Selection */}
          <div>
            <label className="block text-sm font-condensed text-foreground-secondary mb-2">
              Persona ({filteredPersonas.length} available)
            </label>
            <div className="grid grid-cols-2 gap-2 max-h-48 overflow-auto p-2 bg-background-tertiary rounded border border-border">
              {filteredPersonas.map((persona) => (
                <button
                  key={persona.id}
                  onClick={() => setSelectedPersona(persona)}
                  className={`text-left px-3 py-2 rounded text-sm transition-colors ${selectedPersona?.id === persona.id
                    ? 'bg-accent/20 border border-accent text-accent'
                    : 'bg-background hover:bg-background-hover border border-border'
                    }`}
                >
                  <div className="font-condensed font-semibold">{persona.name}</div>
                  <div className="text-xs text-foreground-muted">{persona.role}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Persona Preview */}
          {selectedPersona && (
            <div className="p-4 bg-background-tertiary border border-border rounded">
              <h3 className="text-sm font-condensed font-semibold mb-2">{selectedPersona.name}</h3>
              <p className="text-xs text-foreground-muted mb-3">{selectedPersona.description}</p>
              <div className="text-xs">
                <span className="text-foreground-secondary">Recommended models: </span>
                <span className="text-foreground-muted">
                  {selectedPersona.recommendedModels.join(', ')}
                </span>
              </div>
            </div>
          )}

          {/* Model Selection */}
          <div>
            <label className="block text-sm font-condensed text-foreground-secondary mb-2">
              LLM Model
            </label>
            <select
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
              className="w-full px-3 py-2 bg-background-tertiary border border-border rounded text-sm focus:outline-none focus:border-accent"
            >
              <option value="">Select a model...</option>
              {OPENROUTER_MODELS.map((model) => (
                <option key={model.id} value={model.id}>
                  {model.name} {(model as any).supportsThinking ? '(Thinking)' : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Thinking Mode Toggle */}
          {selectedModel && (OPENROUTER_MODELS.find(m => m.id === selectedModel) as any)?.supportsThinking && (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="thinking-mode"
                checked={thinkingMode}
                onChange={(e) => setThinkingMode(e.target.checked)}
                className="w-4 h-4"
              />
              <label htmlFor="thinking-mode" className="text-sm text-foreground-secondary cursor-pointer">
                Enable Thinking Mode (extended reasoning)
              </label>
            </div>
          )}

          {/* System Prompt Preview */}
          {selectedPersona && (
            <div>
              <label className="block text-sm font-condensed text-foreground-secondary mb-2">
                System Prompt Preview
              </label>
              <div className="p-3 bg-background border border-border rounded text-xs font-mono text-foreground-muted max-h-48 overflow-auto">
                {selectedPersona.systemPromptTemplate}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-border p-4 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="btn-secondary px-4 py-2"
          >
            Cancel
          </button>
          <button
            onClick={handleAdd}
            disabled={!selectedPersona || !selectedModel}
            className="btn-primary px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Add Peer
          </button>
        </div>
      </div>
    </div>
  )
}

